import { itineraryStatusPresentations } from "../config";
import {
  fetchFinalizedBudgetQuoteVersions as fetchFinalizedBudgetQuoteVersionsInRepo,
  fetchItineraryListPage,
  parseItineraryNlQuery as parseItineraryNlQueryInRepo,
} from "../repo";
import { notifyItinerarySales as notifyItinerarySalesInRepo } from "../repo/itineraryRepo";
import {
  listDispatcherUserCandidates,
  listMajorCandidates,
  listPointCandidates,
  listSalesUserCandidates,
} from "../repo/itineraryRepo";
import type {
  ItineraryListPage,
  ItineraryListParams,
  FinalizedBudgetQuoteVersion,
  ItineraryNlQueryParseResult,
  ItineraryStatusPresentation,
  MajorCandidate,
  PointCandidateResult,
  StaffUserCandidate,
  ItinerarySalesNotificationResult,
} from "../types";

/** service 层导出入口（由脚手架生成）。 */
export * from "./itineraryEditorService";
export * from "./itineraryAdvancedService";
export * from "./itineraryOutputService";
export * from "./itineraryPdfService";
export * from "./itineraryListSearchService";
export * from "./itineraryTransportTime";
export * from "./itineraryLocalTransportService";
export * from "./itineraryProfessionalService";
export * from "./itinerarySummaryService";

/** 查询并映射课程线路分页数据。 */
export async function fetchItineraryList(
  params: ItineraryListParams,
): Promise<ItineraryListPage> {
  return fetchItineraryListPage(params);
}

/** 查询当前行程全部已定稿核价及其对应报价。 */
export async function fetchFinalizedBudgetQuoteVersions(
  itineraryId: string,
): Promise<FinalizedBudgetQuoteVersion[]> {
  return fetchFinalizedBudgetQuoteVersionsInRepo(itineraryId);
}

/** 向课程线路所属项目的销售人员发送微信通知。 */
export async function notifyItinerarySales(
  projectId: string,
  itineraryId: string,
): Promise<ItinerarySalesNotificationResult> {
  return notifyItinerarySalesInRepo(projectId, itineraryId);
}

/** 解析并规整课程线路自然语言筛选。 */
export async function parseItineraryNlQuery(query: string): Promise<ItineraryNlQueryParseResult> {
  return parseItineraryNlQueryInRepo(query.trim());
}

/** 按关键词查询课程线路筛选中的专业候选。 */
export async function fetchItineraryMajorCandidates(keyword: string): Promise<MajorCandidate[]> {
  return listMajorCandidates({ keyword: keyword.trim(), limit: 20 });
}

/** 查询课程线路列表销售筛选下拉初始候选。 */
export async function fetchItinerarySalesUserCandidates(keyword = ""): Promise<StaffUserCandidate[]> {
  return listSalesUserCandidates({ keyword: keyword.trim() || undefined, limit: 500 });
}

/** 查询课程线路列表计调筛选下拉初始候选。 */
export async function fetchItineraryDispatcherUserCandidates(
  keyword = "",
): Promise<StaffUserCandidate[]> {
  return listDispatcherUserCandidates({ keyword: keyword.trim() || undefined, limit: 500 });
}

/** 按关键词查询课程线路筛选中的考察点或住宿点候选。 */
export async function fetchItineraryPointCandidates(
  kind: "point" | "stay_area",
  keyword: string,
  provinceNames: string[] = [],
  signal?: AbortSignal,
): Promise<PointCandidateResult> {
  return listPointCandidates({
    kind,
    keyword: keyword.trim() || undefined,
    provinceNames: kind === "point" ? provinceNames : undefined,
    ...(!keyword.trim() ? { page: 1, pageSize: kind === "point" ? 50 : 20 } : {}),
  }, signal);
}

/** 获取行程状态展示信息。 */
export function resolveItineraryStatus(status: string): ItineraryStatusPresentation {
  return itineraryStatusPresentations[status] ?? { label: status || "--", color: "gray" };
}

/** 按列表口径格式化北京时间。 */
export function formatItineraryCreatedAt(value: string): string {
  if (!value) return "--";
  const normalized = value.replace("T", " ");
  return `${normalized.slice(0, 10)} ${normalized.slice(11, 13)}时`;
}
